import React, { useState } from "react";
import {
    TableRow,
    TableCell,
    Box,
    Typography,
    IconButton,
    Avatar,
    Chip,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { Edit, Delete, KeyboardArrowDown, KeyboardArrowUp, RemoveCircle } from "@mui/icons-material";
import { UserWithRole } from "../../../../../models/User";
import { formatTimestamp } from "../../../../../utils/customFunctions";
import { PermissionWrapper } from "../../../../../components/PermissionWrapper";
import { UsersService } from "../../../../../services/supabase/UsersService";
import { UserEditor } from "./UserEditor";
import toastService from "../../../../../services/toast/ToastService";

interface UserRowProps {
    user: UserWithRole;
}

export const UserRow: React.FC<UserRowProps> = ({ user }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { deleteUser } = UsersService();
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);

    const handleDelete = async () => {
        if (window.confirm(`Are you sure you want to delete user "${user.firstName} ${user.lastName}"?`)) {
            const success = await deleteUser(user.id);
            if (success) {
                toastService.showSuccess(`User "${user.firstName} ${user.lastName}" deleted successfully`);
            }
        }
    };

    const getUserInitials = () => {
        return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    };

    const getRoleColor = () => {
        if (user.isSystemRole) {
            switch (user.roleName) {
                case 'admin': return 'error';
                case 'moderator': return 'warning';
                case 'public': return 'default';
                default: return 'primary';
            }
        }
        return 'secondary';
    };

    const IconToShow = () => {
        if (isDeleted) {
            return <RemoveCircle sx={{ color: theme.palette.error.main }} />;
        }
        return open ? <KeyboardArrowUp /> : <KeyboardArrowDown />;
    };

    return (
        <React.Fragment>
            <TableRow 
                hover
                sx={{
                    "& > *": { borderBottom: "unset" },
                    opacity: isDeleted ? 0.5 : 1,
                    backgroundColor: isDeleted
                        ? theme.palette.action.disabledBackground
                        : theme.palette.background.paper
                }}
            >
            {/* User Column - includes email on mobile */}
            <TableCell component="th" scope="user">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                        sx={{ 
                            width: isMobile ? 28 : 32, 
                            height: isMobile ? 28 : 32, 
                            mr: isMobile ? 1.5 : 2,
                            bgcolor: theme.palette.primary.main,
                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                            color: theme.palette.primary.contrastText
                        }}
                    >
                        {getUserInitials()}
                    </Avatar>
                    <Box>
                        <Typography 
                            variant="body2"
                            fontWeight="bold"
                            sx={{ 
                                fontSize: isMobile ? 12 : 13,
                                fontFamily: "primaryFont",
                                letterSpacing: 0.5
                            }}
                        >
                            {user.firstName} {user.lastName}
                        </Typography>
                        {/* Mobile: Show email below name */}
                        {isMobile && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: 10,
                                    fontFamily: "primaryFont",
                                    color: theme.palette.text.secondary,
                                    display: 'block',
                                    mt: 0.25,
                                    maxWidth: 150,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {user.email}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </TableCell>
            {/* Email - hidden on mobile */}
            <TableCell align="left" sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>
                {user.email}
            </TableCell>
            {/* Role + Actions on mobile */}
            <TableCell align={isMobile ? 'right' : 'left'}>
                {isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25 }}>
                        <Chip 
                            label={user.roleName}
                            color={getRoleColor() as any}
                            size="small"
                            variant={user.isSystemRole ? "filled" : "outlined"}
                            sx={{ fontSize: 10, height: 24 }}
                        />
                        <Typography
                            sx={{
                                color: theme.palette.text.disabled,
                                fontSize: 16,
                                fontWeight: 300,
                                mx: 0.25
                            }}
                        >
                            |
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <PermissionWrapper screen="users" permission="update">
                                <IconButton 
                                    size="small" 
                                    onClick={() => setOpen(!open)}
                                    disabled={isDeleted}
                                    sx={{ 
                                        p: 1,
                                        backgroundColor: theme.palette.action.hover,
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.selected
                                        }
                                    }}
                                >
                                    <Edit sx={{ fontSize: 16 }} />
                                </IconButton>
                            </PermissionWrapper>
                            <PermissionWrapper screen="users" permission="delete">
                                <IconButton 
                                    size="small" 
                                    onClick={handleDelete}
                                    color="error"
                                    disabled={isDeleted}
                                    sx={{ 
                                        p: 1,
                                        backgroundColor: theme.palette.error.main + '15',
                                        '&:hover': {
                                            backgroundColor: theme.palette.error.main + '30'
                                        }
                                    }}
                                >
                                    <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                            </PermissionWrapper>
                        </Box>
                    </Box>
                ) : (
                    <Chip 
                        label={user.roleName}
                        color={getRoleColor() as any}
                        size="small"
                        variant={user.isSystemRole ? "filled" : "outlined"}
                    />
                )}
            </TableCell>
            <TableCell align="left" sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>
                {user.mobileNumber}
            </TableCell>
            <TableCell align="left" sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>
                {formatTimestamp(user.created)}
            </TableCell>
            {/* Actions - hidden on mobile, shown in Role column */}
            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <PermissionWrapper screen="users" permission="update">
                    <IconButton 
                        size="small" 
                        onClick={() => setOpen(!open)}
                        disabled={isDeleted}
                        sx={{ mr: 1 }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                </PermissionWrapper>
                <PermissionWrapper screen="users" permission="delete">
                    <IconButton 
                        size="small" 
                        onClick={handleDelete}
                        color="error"
                        disabled={isDeleted}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </PermissionWrapper>
            </TableCell>
        </TableRow>
        {
            <UserEditor
                user={user}
                open={open}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
            />
        }
        </React.Fragment>
    );
};
